import React, { FC, useEffect } from 'react';
import { Button, Menu } from 'antd';
import { Copy } from 'common/svg';
import { CardCollapse } from 'common/components';
import { InfiniteScroll, Spin } from 'common/components';
import { Divider } from 'common/components/Divider';
import { countFormatter, dateFormatter, priceFormatter } from 'common/helpers/formatter.helper';
import { LIST_LIMIT_0 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as CloseIcon } from 'app/assets/images/redesign/close.svg';
import { ReactComponent as ArrowRightLongIcon } from 'app/assets/images/redesign/arrow-right-long.svg';
import { ERequestGoodsInfoMenuKey } from 'entities/Request/Request.enum';
import { getRequestGoodsCardInfo } from 'entities/Request/Request.helper';
import { IRequest } from 'entities/Request/Request.models';
import { GoodsCardMin } from 'entities/Goods/components/GoodsCardMin';
import { ERequestHistoryType } from 'entities/RequestHistory/RequestHistory.enum';
import { getPriceChangeHistory, selectPriceChangeHistory } from 'entities/RequestHistory/RequestHistory.slice';
import { selectIsSupplyLoading, selectSupply } from 'entities/Supply/Supply.slice';

interface IComponentProps {
  open: boolean;
  request: IRequest;
  onClose: () => void;
}

export const RequestGoodsCard: FC<IComponentProps> = ({ open, request, onClose }) => {
  const dispatch = useAppDispatch();
  // requestHistory
  const priceChangeHistory = useAppSelector(selectPriceChangeHistory);
  // supply
  const supply = useAppSelector(selectSupply);
  const isSupplyLoading = useAppSelector(selectIsSupplyLoading);

  const { count, amount, amountWithTaxes } = getRequestGoodsCardInfo(supply);

  useEffect(() => {
    if (supply) {
      dispatch(
        getPriceChangeHistory({
          requestId: request.id,
          supplyId: supply.id,
          type: ERequestHistoryType.SupplyPriceChanged,
          limit: LIST_LIMIT_0,
        }),
      );
    }
  }, [supply]);

  const copySellerCode = (e: any) => {
    e.stopPropagation();

    if (!supply?.good?.sellerCode) {
      return;
    }

    navigator.clipboard.writeText(supply.good.sellerCode);
  };

  return (
    <div className={`request-goods-card ${open ? 'opened' : ''}`}>
      <div className="request-goods-card__header">
        <Button className="button-circle secondary" icon={<CloseIcon />} onClick={onClose} />
      </div>

      <div className="flex direction-column request-goods-card__body">
        <InfiniteScroll>
          <Spin spinning={isSupplyLoading}>
            <div className="text-h1-drawers mb-11" style={{ letterSpacing: -1 }}>
              Информация о товаре
            </div>

            <Menu
              items={[
                {
                  label: 'Общая информация',
                  key: ERequestGoodsInfoMenuKey.General,
                },
              ]}
              selectedKeys={[ERequestGoodsInfoMenuKey.General]}
              mode="horizontal"
            />

            <div className="mb-6">
              <GoodsCardMin good={supply?.good} canBeOpened={!!supply?.good.categories?.length} />
            </div>

            <div className="mb-19 request-goods-card__block">
              <div className="request-goods-card__block-row">
                <span className="text-controls color-dark-grey">Товаров в заказе</span>

                <span className="text-controls">{countFormatter(count)}</span>
              </div>

              <div className="request-goods-card__block-row">
                <span className="text-controls color-dark-grey">Стоимость товаров</span>

                <span className="text-controls">{priceFormatter(amount)}</span>
              </div>

              <div className="request-goods-card__block-row">
                <span className="text-controls color-dark-grey">Сумма НДС</span>

                <span className="text-controls">{priceFormatter(amountWithTaxes - amount)}</span>
              </div>

              <div className="request-goods-card__block-row">
                <span className="text-controls color-dark-grey">Итого с НДС</span>

                <span className="text-controls">{priceFormatter(amountWithTaxes)}</span>
              </div>

              <div className="request-goods-card__block-row">
                <span className="text-controls color-dark-grey">Поставщик</span>

                <span className="text-controls">{supply?.seller.name}</span>
              </div>

              {supply?.good?.sellerCode && (
                <div className="request-goods-card__block-row">
                  <span className="text-controls color-dark-grey">Артикул</span>

                  <span className="text-controls flex align-center">
                    {supply.good.sellerCode}
                    <Copy className="ml-1 icon-copy-dark-grey cursor-pointer" onClick={copySellerCode} />
                  </span>
                </div>
              )}
            </div>

            {!!priceChangeHistory.length && (
              <>
                <Divider marginY={20} />

                <CardCollapse
                  keys={['history']}
                  items={[
                    {
                      key: 'history',
                      label: <span className="text-h4">Корректировки цены</span>,
                      children: (
                        <div className="mb-19 request-goods-card__block">
                          {priceChangeHistory.map((history) => {
                            return (
                              <div key={history.id} className="request-goods-card__block-row">
                                <span className="text-controls color-dark-grey">
                                  {dateFormatter('DD.MM.YYYY', history.createdAt)}
                                </span>

                                <div className="request-goods-card__block-row-container">
                                  <span className="text-controls">{priceFormatter(history.changes?.oldPrice)}</span>

                                  <ArrowRightLongIcon className="icon-arrow-right-long-dark-grey" />

                                  <span className="text-controls">{priceFormatter(history.changes?.price)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </Spin>
        </InfiniteScroll>
      </div>
    </div>
  );
};
